import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMerchant } from "context/merchant";
import AddressSection from "./components/AddressSection";
import { createManualOrder, getMerchantAddress } from "./api/deliveryApi";
import DeliverySizeCardList from "./components/DeliverySizeCardList";
import ConfirmModal from "./components/ConfirmModal";
import QrCodeModal from "./components/QrCodeModal";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  pickupDistrictId: Yup.string().required("Илгээмж ачих байршлаа сонгоно уу"),
  pickupDistrictName: Yup.string().required(),
  pickupAddress: Yup.string().required("Илгээмж ачих хаягаа оруулна уу"),
  pickupPhone: Yup.string()
    .matches(/^\d{8}$/, "8 оронтой утасны дугаар оруулна уу")
    .required("Илгээж ачих утасны дугаараа оруулна уу"),
  dropDistrictId: Yup.string().required("Хүргэх байршилаа сонгоно уу"),
  dropDistrictName: Yup.string().required(),
  dropAddress: Yup.string().required("Хүргэх хаягаа оруулна уу"),
  dropPhone: Yup.string()
    .matches(/^\d{8}$/, "8 оронтой утасны дугаар оруулна уу")
    .required("Хүргэх утасны дугаараа оруулна уу"),
  size: Yup.string().required("Ачааны төрлөө сонгоно уу"),
});

export default function RequestDriverScreen() {
  const { isFullScreen, toggleFullScreen } = useMerchant();
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [selectedSizeInfo, setSelectedSizeInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newOrderId, setNewOrderId] = useState(null);
  const [deliveryPrice, setDeliveryPrice] = useState(null);

  const formik = useFormik({
    initialValues: {
      pickupDistrictId: "",
      pickupDistrictName: "",
      pickupAddress: "",
      pickupPhone: "",
      dropDistrictId: "",
      dropDistrictName: "",
      dropAddress: "",
      dropPhone: "",
      deliveryPrice: "",
      size: "",
    },
    validationSchema,
    onSubmit: () => setShowConfirm(true),
  });

  useEffect(() => {
    const loadMerchant = async () => {
      try {
        const res = await getMerchantAddress();
        const merchant = res.data;
        if (merchant) {
          formik.setValues((prev) => ({
            ...prev,
            pickupDistrictId: merchant.district?.id || "",
            pickupDistrictName: merchant.district?.name || "",
            pickupAddress: merchant.address || "",
            pickupPhone: merchant.phone || "",
          }));
        }
      } catch (err) {
        console.error("Merchant info татаж чадсангүй:", err);
      } finally {
        setLoading(false);
      }
    };
    loadMerchant();
  }, []);

  // ✅ Захиалга баталгаажуулах
  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const finalDeliveryPrice =
        Number(formik.values.deliveryPrice) || Number(deliveryPrice);

      if (!finalDeliveryPrice || finalDeliveryPrice <= 0) {
        toast.error("Төлбөрийн дүн буруу байна!");
        setSubmitting(false);
        return;
      }

      const payload = {
        ...formik.values,
        deliveryPrice: finalDeliveryPrice,
      };

      const { data } = await createManualOrder(payload);

      if (!data?.id) {
        toast.error("Захиалга үүсгэхэд алдаа гарлаа");
        setSubmitting(false);
        return;
      }

      setNewOrderId(data.id);
      toast.success("Захиалга амжилттай илгээгдлээ");

      setTimeout(() => setShowQR(true), 300);
    } catch (err) {
      console.error("createManualOrder error:", err);
      toast.error("Захиалга илгээхэд алдаа гарлаа");
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="container-fluid">
      {!isFullScreen && (
        <div className="page-title-box d-flex justify-content-between align-items-center">
          <h4 className="page-title">Жолооч дуудах</h4>
          <button className="btn btn-primary" onClick={toggleFullScreen}>
            <i className="ri ri-fullscreen-line" />
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-body pt-1">
          <form onSubmit={formik.handleSubmit}>
            <div className="row">
              <AddressSection
                title="Илгээмж ачих хаяг"
                loading={loading}
                districtIdField="pickupDistrictId"
                districtNameField="pickupDistrictName"
                address="pickupAddress"
                phone="pickupPhone"
                formik={formik}
              />
              <AddressSection
                title="Хүргэх хаяг"
                loading={loading}
                districtIdField="dropDistrictId"
                districtNameField="dropDistrictName"
                address="dropAddress"
                phone="dropPhone"
                formik={formik}
              />
            </div>

            <h5 className="mt-4 mb-2">Ачааны төрөл</h5>
            <DeliverySizeCardList
              districtId={formik.values.dropDistrictId}
              selected={formik.values.size}
              onSelect={(val, fullItem) => {
                formik.setFieldValue("size", val);
                formik.setFieldTouched("size", true);
                formik.setFieldValue("deliveryPrice", fullItem?.price || "");
                setDeliveryPrice(fullItem?.price || null);
                setSelectedSizeInfo(fullItem);
              }}
            />

            {formik.touched.size && formik.errors.size && (
              <div className="text-danger small ps-2">{formik.errors.size}</div>
            )}

            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-danger px-4"
                disabled={loading || submitting}
              >
                <i className="bi bi-credit-card me-1" /> Баталгаажуулах
              </button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        values={formik.values}
        selectedSizeInfo={selectedSizeInfo}
        loading={submitting}
      />

      <QrCodeModal
        show={showQR}
        onClose={() => setShowQR(false)}
        orderId={newOrderId}
        deliveryPrice={Number(formik.values.deliveryPrice) || deliveryPrice}
        setDeliveryPrice={setDeliveryPrice}
      />
    </div>
  );
}
